import React, { useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadialBarChart, RadialBar
} from 'recharts';
import { AlertCircle, CheckCircle, Clock, TrendingUp, Link as LinkIcon, MessageSquare, X } from 'react-feather';

// Add new interface for modal state
interface BlockerModalState {
  isOpen: boolean;
  blocker?: {
    issue: string;
    contacts: Array<{ name: string; role: string; slack: string; }>;
    relatedLinks: Array<{ title: string; type: string; url: string; }>;
    recommendedSteps?: string[];
  };
}

export default function TeamProgress() {
  const [selectedProject, setSelectedProject] = useState('all');
  const [modal, setModal] = useState<BlockerModalState>({ isOpen: false });

  // Updated sample data structure with contacts and links
  const projectHealth = [
    {
      name: 'Airport Face Recognition',
      completion: 68,
      blockers: 3,
      velocity: 85,
      recentBlockers: [
        {
          issue: "High false positive rates for VIP passengers",
          contacts: [
            { name: "Dr. Sarah Chen", role: "ML Lead", slack: "@sarahc" },
            { name: "Mike Roberts", role: "VIP Services Lead", slack: "@mroberts" }
          ],
          relatedLinks: [
            { title: "VIP Detection Analysis", type: "notion", url: "/vip-analysis" },
            { title: "FR-238", type: "jira", url: "/browse/FR-238" }
          ],
          recommendedSteps: [
            "1. Review VIP Detection Analysis document with Dr. Chen",
            "2. Coordinate with Mike Roberts to get latest VIP passenger data",
            "3. Schedule model retraining session",
            "4. Run validation tests with new VIP dataset"
          ]
        },
        {
          issue: "Issues with passengers wearing hats",
          contacts: [
            { name: "James Wilson", role: "CV Engineer", slack: "@jwilson" }
          ],
          relatedLinks: [
            { title: "Headwear Detection Spec", type: "notion", url: "/headwear-spec" },
            { title: "FR-242", type: "jira", url: "/browse/FR-242" }
          ]
        },
        {
          issue: "Thermal imaging integration delays",
          contacts: [
            { name: "Lisa Park", role: "Integration Lead", slack: "@lpark" },
            { name: "Alex Kumar", role: "Hardware Specialist", slack: "@akumar" }
          ],
          relatedLinks: [
            { title: "Integration Timeline", type: "notion", url: "/thermal-timeline" },
            { title: "FR-245", type: "jira", url: "/browse/FR-245" }
          ]
        }
      ],
      insights: "Team velocity increased after resolving edge device optimization issues. Current focus on thermal imaging integration showing promise."
    },
    {
      name: 'Pedestrian Detection (AV)',
      completion: 72,
      blockers: 2,
      velocity: 90,
      recentBlockers: [
        {
          issue: "Detection box jitter during movement",
          contacts: [
            { name: "Tom Zhang", role: "Computer Vision Lead", slack: "@tzhang" },
            { name: "Emma Davis", role: "QA Lead", slack: "@edavis" }
          ],
          relatedLinks: [
            { title: "Jitter Analysis Doc", type: "notion", url: "/jitter-analysis" },
            { title: "PD-156", type: "jira", url: "/browse/PD-156" }
          ]
        },
        {
          issue: "Inconsistent lighting at suburban crosswalks",
          contacts: [
            { name: "Priya Patel", role: "ML Engineer", slack: "@ppatel" },
            { name: "Chris Wong", role: "Data Scientist", slack: "@cwong" }
          ],
          relatedLinks: [
            { title: "Lighting Conditions Study", type: "notion", url: "/lighting-study" },
            { title: "PD-158", type: "jira", url: "/browse/PD-158" }
          ],
          recommendedSteps: [
            "1. Review the Lighting Conditions Study with Priya Patel",
            "2. Verify sensor calibration and analyze lighting data trends",
            "3. Collaborate with Chris Wong to adjust the detection algorithm",
            "4. Test the updated model under varied lighting conditions"
          ]
        }
      ],
      insights: "Strong progress on lighting condition handling. QA feedback pending on latest improvements."
    },
    {
      name: 'Retail Safety Analysis',
      completion: 55,
      blockers: 4,
      velocity: 65,
      recentBlockers: [
        {
          issue: "Edge device optimization discrepancies",
          contacts: [
            { name: "Maria Garcia", role: "Edge Computing Lead", slack: "@mgarcia" },
            { name: "David Kim", role: "Performance Engineer", slack: "@dkim" }
          ],
          relatedLinks: [
            { title: "Edge Optimization Plan", type: "notion", url: "/edge-opt" },
            { title: "RS-342", type: "jira", url: "/browse/RS-342" }
          ]
        },
        {
          issue: "Test environment inconsistencies",
          contacts: [
            { name: "Ryan Foster", role: "DevOps Lead", slack: "@rfoster" }
          ],
          relatedLinks: [
            { title: "Test Env Setup", type: "notion", url: "/test-env" },
            { title: "RS-345", type: "jira", url: "/browse/RS-345" }
          ]
        },
        {
          issue: "Integration delays with existing systems",
          contacts: [
            { name: "Sophie Chen", role: "Integration Engineer", slack: "@schen" },
            { name: "Bob Wilson", role: "Systems Architect", slack: "@bwilson" }
          ],
          relatedLinks: [
            { title: "Integration Roadmap", type: "notion", url: "/integration-plan" },
            { title: "RS-348", type: "jira", url: "/browse/RS-348" }
          ]
        },
        {
          issue: "Performance issues in high-traffic scenarios",
          contacts: [
            { name: "Alex Johnson", role: "Performance Lead", slack: "@ajohnson" }
          ],
          relatedLinks: [
            { title: "Performance Analysis", type: "notion", url: "/perf-analysis" },
            { title: "RS-350", type: "jira", url: "/browse/RS-350" }
          ]
        }
      ],
      insights: "Team addressing edge computing challenges. Maria Garcia leading optimization efforts."
    }
  ];

  // Add historical data for charts
  const velocityTrends = {
    labels: ['Sprint 1', 'Sprint 2', 'Sprint 3', 'Sprint 4', 'Sprint 5'],
    datasets: [
      {
        name: 'Face Recognition',
        data: [65, 75, 85, 82, 85]
      },
      {
        name: 'Pedestrian Detection',
        data: [70, 75, 82, 88, 90]
      },
      {
        name: 'Retail Safety',
        data: [45, 52, 58, 62, 65]
      }
    ]
  };

  const burndownData = {
    dates: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    ideal: [100, 75, 50, 25, 0],
    actual: [100, 85, 65, 45, 30]
  };

  // Add Modal Component
  const BlockerModal = () => {
    if (!modal.isOpen || !modal.blocker) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-semibold text-gray-900">{modal.blocker.issue}</h3>
            <button 
              onClick={() => setModal({ isOpen: false })}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Recommended Workflow */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-800 mb-3">Recommended Workflow</h4>
            <ul className="space-y-3">
              {modal.blocker.recommendedSteps?.map((step, index) => (
                <li key={index} className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3">
                    {index + 1}
                  </div>
                  <span className="text-gray-700">{step}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Key Contacts */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-800 mb-3">Key Contacts</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {modal.blocker.contacts.map((contact, idx) => (
                <div key={idx} className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{contact.name}</p>
                    <p className="text-sm text-gray-600">{contact.role}</p>
                    <p className="text-sm text-blue-600">{contact.slack}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Related Resources */}
          <div>
            <h4 className="text-md font-medium text-gray-800 mb-3">Related Resources</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {modal.blocker.relatedLinks.map((link, idx) => (
                <a
                  key={idx}
                  href={link.url}
                  className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <LinkIcon className="w-4 h-4 text-purple-600 mr-2" />
                  <span className="text-gray-900">{link.title}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Project Health Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {projectHealth.map(project => (
          <div 
            key={project.name}
            className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer"
            onClick={() => setSelectedProject(project.name)}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
              <div className={`px-3 py-1 rounded-full text-sm font-medium
                ${project.blockers > 2 ? 'bg-red-100 text-red-800' : 
                  project.blockers > 0 ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-green-100 text-green-800'}`}>
                {project.blockers} Blockers
              </div>
            </div>
            
            <div className="flex items-center mb-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    project.completion > 75 ? 'bg-green-500' :
                    project.completion > 50 ? 'bg-blue-500' :
                    'bg-yellow-500'
                  }`}
                  style={{ width: `${project.completion}%` }}
                />
              </div>
              <span className="ml-2 text-sm text-gray-600">{project.completion}%</span>
            </div>

            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <TrendingUp className="w-4 h-4 mr-2" />
                <span>Velocity: {project.velocity}/100</span>
              </div>
              <div className="text-sm text-gray-500">{project.insights}</div>
            </div>

            {/* Blockers List */}
            {project.blockers > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Recent Blockers:</h4>
                <ul className="space-y-4">
                  {project.recentBlockers.map((blocker, idx) => (
                    <li 
                      key={idx} 
                      className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => setModal({ isOpen: true, blocker })}
                    >
                      <div className="flex items-start">
                        <AlertCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="space-y-2 w-full">
                          <span className="text-sm font-medium text-gray-800">{blocker.issue}</span>
                          
                          {/* Contacts */}
                          <div className="flex flex-wrap gap-2">
                            {blocker.contacts.map((contact, cidx) => (
                              <div key={cidx} className="flex items-center bg-white px-2 py-1 rounded border border-gray-200 text-xs shadow-sm">
                                <MessageSquare className="w-3 h-3 mr-1 text-blue-600" />
                                <span className="font-semibold text-gray-900">{contact.name}</span>
                                <span className="mx-1 text-gray-400">|</span>
                                <span className="text-gray-700">{contact.slack}</span>
                              </div>
                            ))}
                          </div>

                          {/* Related Links */}
                          <div className="flex flex-wrap gap-2">
                            {blocker.relatedLinks.map((link, lidx) => (
                              <a
                                key={lidx}
                                href={link.url}
                                className="flex items-center bg-white px-2 py-1 rounded border border-gray-200 text-xs shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-colors"
                              >
                                <LinkIcon className="w-3 h-3 mr-1 text-purple-600" />
                                <span className="font-medium text-gray-800">{link.title}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* RAG Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Health Overview</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={projectHealth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="completion" name="Progress" fill="#3B82F6" />
              <Bar dataKey="blockers" name="Blockers" fill="#EF4444" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Velocity Trends */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Velocity Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={velocityTrends.labels.map((label, idx) => ({
              name: label,
              ...velocityTrends.datasets.reduce((acc, dataset) => ({
                ...acc,
                [dataset.name]: dataset.data[idx]
              }), {})
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              {velocityTrends.datasets.map((dataset, idx) => (
                <Line
                  key={dataset.name}
                  type="monotone"
                  dataKey={dataset.name}
                  stroke={['#3B82F6', '#10B981', '#8B5CF6'][idx]}
                  strokeWidth={2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sprint Burndown */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sprint Burndown</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={burndownData.dates.map((date, idx) => ({
            name: date,
            Ideal: burndownData.ideal[idx],
            Actual: burndownData.actual[idx]
          }))}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="Ideal"
              stroke="#9CA3AF"
              strokeDasharray="5 5"
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="Actual"
              stroke="#3B82F6"
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Add Modal to the JSX */}
      <BlockerModal />
    </div>
  );
} 